import { ContentObjectItem } from "@vertesia/common"
import { toast } from "sonner"
import { Md5 } from "ts-md5"

import { useUserSession } from "@session/UserSession"

export interface DocumentUploadResult {
    success: boolean
    objects: ContentObjectItem[]
    uploadedFiles: UploadedFileInfo[]
    skippedFiles: UploadedFileInfo[]
    failedFiles: UploadedFileInfo[]
}

export interface FileUploadStatus {
    file: File
    status: "pending" | "uploading" | "success" | "error"
    progress: number
    message?: string
    id?: string
    action?: "create" | "update" | "skip"
}

export enum FileUploadAction {
    CREATE = "create",
    SKIP = "skip",
    UPDATE = "update",
}

export interface FileWithMetadata {
    file: File
    hash?: string
    name: string
    size: number
    location?: string
    existingId?: string
    action?: FileUploadAction
    metadata?: Record<string, unknown>
}

export interface UploadedFileInfo {
    id?: string
    name: string
    location?: string
    status: "created" | "updated" | "skipped" | "failed"
    type: string | null
    error?: string
}

async function calculateFileHash(file: File): Promise<string | undefined> {
    try {
        const arrayBuffer = await file.arrayBuffer()
        const md5 = new Md5()
        md5.appendByteArray(new Uint8Array(arrayBuffer))
        const hash = md5.end()
        return hash?.toString()
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"
        console.error("Error calculating file hash:", errorMessage)
        return undefined
    }
}

async function prepareFilesWithMetadata(files: File[], selectedFolder?: string | null): Promise<FileWithMetadata[]> {
    const filesWithMetadata: FileWithMetadata[] = []

    const batchSize = 5
    for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, i + batchSize)

        const metadataResults = await Promise.all(
            batch.map(async (file) => {
                let location = selectedFolder || undefined

                const relativePath = (file as File).webkitRelativePath
                if (relativePath) {
                    const pathParts = relativePath.split("/")

                    if (pathParts.length > 1) {
                        const folderPath = pathParts.slice(0, -1).join("/")
                        location = selectedFolder ? `${selectedFolder}/${folderPath}` : folderPath
                    }
                }

                const hash = await calculateFileHash(file)

                return {
                    file,
                    hash,
                    name: file.name,
                    size: file.size,
                    location,
                }
            })
        )
        filesWithMetadata.push(...metadataResults)
    }
    return filesWithMetadata
}

export function useSmartFileUploadProcessing() {
    const { client } = useUserSession()

    const prepareFilesForUpload = async (files: File[], selectedFolder?: string | null, limitToCollectionId?: string): Promise<FileWithMetadata[]> => {
        try {
            const filesWithMetadata = await prepareFilesWithMetadata(files, selectedFolder)

            const identifyExistingHash = async () => {
                const query = {
                    "content.etag": { $in: filesWithMetadata.map((file) => file.hash) },
                }

                let res: ContentObjectItem[]

                if (limitToCollectionId) {
                    res = (await client.store.collections.searchMembers(limitToCollectionId, {
                        query: {
                            match: query,
                        },
                        select: undefined,
                    })).results
                } else {
                    res = (await client.store.objects.search({
                        query: { match: query },
                        select: undefined,
                    })).results
                }

                for (const doc of res) {
                    const file = filesWithMetadata.find((f) => f.hash === doc.content.etag)
                    if (file) {
                        file.existingId = doc.id
                        file.action = FileUploadAction.SKIP
                    }
                }
            }

            const identifyExistingIds = async () => {
                const unskippedFiles = filesWithMetadata.filter((file) => file.action !== FileUploadAction.SKIP)
                const allLocations = unskippedFiles.map((file) => file.location)
                const uniqueLocations = Array.from(new Set(allLocations))

                const queries = []
                for (const location of uniqueLocations) {
                    const namesInLocation = unskippedFiles.filter((file) => file.location === location).map((file) => file.name)
                    const query = {
                        location: location ?? "",
                        "content.name": { $in: namesInLocation },
                    }
                    if (limitToCollectionId) {
                        const res = client.store.collections.searchMembers(limitToCollectionId, {
                            query: {
                                match: query,
                            },
                            select: undefined,
                        })
                        queries.push(res)
                    } else {
                        const res = client.store.objects.find({
                            query: {
                                match: query,
                            },
                            select: undefined,
                        })
                        queries.push(res)
                    }
                }

                const results = (await Promise.all(queries)).flat()

                for (const doc of results) {
                    if ('content' in doc) {
                        const file = filesWithMetadata.find(
                            (f) =>
                                f.name === doc.content.name &&
                                (f.location ? f.location === doc.location : doc.location === ""),
                        );
                        if (file) {
                            file.existingId = doc.id
                            file.action = FileUploadAction.UPDATE
                        }
                    }
                }
            }

            await identifyExistingHash()
            await identifyExistingIds()

            filesWithMetadata.forEach((f) => {
                if (!f.action) {
                    f.action = FileUploadAction.CREATE
                }
            })

            return filesWithMetadata
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : "Unknown error occurred"
            toast.error("Error in file upload processing check", {
                description: errorMessage,
                duration: 2000,
            })
            throw new Error("Error in file upload processing check: " + errorMessage)
        }
    }

    return {
        checkDocumentProcessing: prepareFilesForUpload,
    }
}
